/**
 * Add-on catalogue — the single source of truth for the OPTIONAL modules a
 * workspace can install (Garment OS, POS, …). Which add-ons a given company has
 * turned on is recorded in the `company_addons` table; this registry describes
 * what's available and which module strings each one unlocks.
 *
 * Adding a future add-on is one entry here + its module list in
 * `@flint/api-contract/modules` — no schema change. The billing "Add-ons"
 * section renders one toggle per entry; `computeEntitlements` (domain) unions
 * the modules of every enabled add-on into the entitlement projection. Keep the
 * `modules` here ALIGNED with the domain `ADDON_MODULES` map (the domain can't
 * import this package — same mirror discipline as PLAN_TIERS ↔ PLAN_CATALOG).
 */
import {
  GARMENT_MODULES,
  POS_MODULES,
  AI_MODULES,
  WHATSAPP_MODULES,
  COMMERCE_MODULES,
} from "./modules.js";

/**
 * Registry keys — also the `company_addons.addon_key` values.
 *
 * The seven after `whatsapp` are the 1.0-withheld modules (ADR-0046) exposed as
 * FREE, default-OFF add-ons so they can be switched on per workspace for demos
 * without a code change. They are NOT installed by default (`installDefaultAddons`
 * leaves them off), so a normally-onboarded customer still sees only the 1.0
 * surface until someone toggles them in Settings → Billing.
 */
export type AddonKey =
  | "garment"
  | "pos"
  | "ai"
  | "whatsapp"
  | "commerce"
  | "payroll"
  | "documents"
  | "reports"
  | "books"
  | "cash"
  | "stock-ops"
  | "design-studio";

/** Lucide icon name the UI maps to a component (keeps this package React-free). */
export type AddonIcon =
  | "shirt"
  | "store"
  | "sparkles"
  | "message-circle"
  | "calendar-clock"
  | "folder-open"
  | "file-bar-chart"
  | "book-open-check"
  | "wallet"
  | "arrow-left-right"
  | "palette";

export interface AddonCatalogEntry {
  key: AddonKey;
  name: string;
  /** One-line description shown under the add-on title. */
  description: string;
  icon: AddonIcon;
  /** Module strings this add-on contributes to `company_entitlements.modules`. */
  modules: readonly string[];
  /** "What's included" lines, shown when the add-on row is expanded. */
  features: readonly string[];
  /**
   * Recurring monthly price in integer paise (GST-inclusive). Charged as a
   * Razorpay subscription add-on on the customer's next invoice when enabled,
   * re-added each cycle. PLACEHOLDER pricing — needs PM/founder sign-off before
   * live billing. `null` = free entitlement toggle (no charge).
   */
  pricePaiseMonthly: number | null;
}

export const ADDON_CATALOG: readonly AddonCatalogEntry[] = [
  {
    key: "ai",
    name: "Flint AI",
    description: "The AI assistant — Ask Flint, daily briefings, channel replies, and extraction.",
    icon: "sparkles",
    modules: AI_MODULES,
    pricePaiseMonthly: null, // DEMO: free so it toggles without a mandate (was 999_00) — restore before billing go-live (ADR-0046)
    features: [
      "Ask Flint — chat with your business data",
      "Daily briefings & cash-flow narration",
      "WhatsApp, Telegram & Slack replies",
      "Document extraction & summaries",
    ],
  },
  {
    key: "garment",
    name: "Garment OS",
    description: "The garment & export vertical — style files, costing, sampling, QC, dispatch.",
    icon: "shirt",
    modules: GARMENT_MODULES,
    pricePaiseMonthly: null, // DEMO: free so it toggles without a mandate (was 2_499_00) — restore before billing go-live (ADR-0046)
    features: [
      "Style Files & costing",
      "Sampling & PP approval",
      "Time & Action calendar",
      "Materials & production",
      "QC inspection & defect tracking",
      "Dispatch & export invoicing",
    ],
  },
  {
    key: "pos",
    name: "Point of Sale",
    description: "Retail checkout, cash drawer, shifts & returns for over-the-counter sales.",
    icon: "store",
    modules: POS_MODULES,
    pricePaiseMonthly: null, // DEMO: free so it toggles without a mandate (was 799_00) — restore before billing go-live (ADR-0046)
    features: [
      "Touch checkout & cash drawer",
      "Shift open/close & cash counts",
      "Returns & manager voids",
      "Walk-in & retail invoices",
    ],
  },
  {
    key: "whatsapp",
    name: "WhatsApp Business",
    description: "Send invoices, reminders & let customers chat with Flint on WhatsApp.",
    icon: "message-circle",
    modules: WHATSAPP_MODULES,
    pricePaiseMonthly: null, // DEMO: free so it toggles without a mandate (was 599_00) — restore before billing go-live (ADR-0046)
    features: [
      "Send invoices & payment links on WhatsApp",
      "Automated payment reminders",
      "Customers reply & pay in-thread",
      "Daily briefings to your WhatsApp",
    ],
    // NOTE: this add-on gates BUSINESS messaging only. Login codes, step-up
    // verification, and security alerts are NOT part of it — they always reach
    // you (SMS floor), billed as platform cost, never metered here. @see
    // WHATSAPP_MODULES (modules.ts) for the security boundary.
  },
  {
    key: "commerce",
    name: "Commerce Store",
    description:
      "The B2B wholesale storefront — Store admin: products, buyers, orders, quotes & samples.",
    icon: "store",
    modules: COMMERCE_MODULES,
    pricePaiseMonthly: null, // DEMO: free toggle while the Store module is built out (V1.0.1).
    features: [
      "Wholesale product catalog & pricing",
      "Buyer accounts & approvals",
      "Orders → proforma in Flint",
      "Quotes & sampling",
    ],
  },

  // ── 1.0-withheld modules as free demo toggles (ADR-0046) ──────────────
  // Free (pricePaiseMonthly: null) so they switch on against any plan/trial
  // with no Razorpay involvement, and default-OFF (not in installDefaultAddons)
  // so a normal customer still sees only the 1.0 surface. Each grants exactly
  // its one module string, which the nav/route/server gates already check.
  {
    key: "payroll",
    name: "Payroll (Preview)",
    description: "Employee master + monthly payroll runs. Returns as a full 1.2 module.",
    icon: "calendar-clock",
    modules: ["payroll"],
    pricePaiseMonthly: null,
    features: ["Employee master & PII vault", "Monthly payroll runs", "Deduction → ledger posting"],
  },
  {
    key: "documents",
    name: "Documents (Preview)",
    description: "Foldered, searchable document management. Returns as a full 1.2 module.",
    icon: "folder-open",
    modules: ["documents"],
    pricePaiseMonthly: null,
    features: ["Folders & search", "Uploads with presigned URLs", "Knowledge-base tagging"],
  },
  {
    key: "reports",
    name: "Reports engine (Preview)",
    description: "Config-driven report library with CSV/PDF export. Returns as a full 1.2 module.",
    icon: "file-bar-chart",
    modules: ["reports"],
    pricePaiseMonthly: null,
    features: ["Report library", "Run with parameters", "CSV & PDF export"],
  },
  {
    key: "books",
    name: "Books (Preview)",
    description: "Trial Balance, P&L, Balance Sheet & journals. Tally XML export stays in 1.0.",
    icon: "book-open-check",
    modules: ["books"],
    pricePaiseMonthly: null,
    features: ["Trial Balance / P&L / Balance Sheet", "Manual journal vouchers", "Period close"],
  },
  {
    key: "cash",
    name: "Cash (Preview)",
    description: "Petty cash, transfers & bank reconciliation. Bank accounts stay in 1.0.",
    icon: "wallet",
    modules: ["cash"],
    pricePaiseMonthly: null,
    features: ["Petty cash & imprest", "Cash transfers", "Bank statement reconciliation"],
  },
  {
    key: "stock-ops",
    name: "Stock transfers (Preview)",
    description: "Warehouse-to-warehouse transfers. Stock, warehouses & adjustments stay in 1.0.",
    icon: "arrow-left-right",
    modules: ["stock-ops"],
    pricePaiseMonthly: null,
    features: ["Inter-warehouse transfers", "In-transit tracking"],
  },
  {
    key: "design-studio",
    name: "Design Studio (Preview)",
    description: "Garment design canvas & tech pack. Returns as 1.1 Garment Design.",
    icon: "palette",
    modules: ["design-studio"],
    pricePaiseMonthly: null,
    features: ["Design canvas & markings", "AI design proposals", "Tech pack generation"],
  },
] as const;

export const ADDON_BY_KEY: Record<string, AddonCatalogEntry> = Object.fromEntries(
  ADDON_CATALOG.map((a) => [a.key, a]),
);

/** Format an add-on's monthly price, e.g. `₹999/mo`, or null when it's free. */
export function formatAddonPrice(entry: AddonCatalogEntry): string | null {
  if (entry.pricePaiseMonthly == null) return null;
  return `₹${(entry.pricePaiseMonthly / 100).toLocaleString("en-IN")}/mo`;
}
